import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ALL_STAGES, getStageConfig, normalizeStatus } from '@/lib/pipelineStages';
import { 
  ArrowLeft, User, Mail, Phone, Linkedin, Globe, 
  FileText, MessageSquare, Send
} from 'lucide-react';

const ApplicationDetailPage = () => {
  const { appId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isRecruiter = ['super_admin', 'client_admin', 'team_leader', 'recruiter'].includes(user?.role);

  useEffect(() => {
    const fetchApplication = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/applications/${appId}`, { 
          headers, 
          withCredentials: true 
        });
        setApplication(response.data);
      } catch (error) {
        toast.error('Application not found');
        navigate('/applications');
      } finally {
        setLoading(false);
      }
    };
    fetchApplication();
  }, [appId, token, navigate]);

  const handleStatusChange = async (status) => {
    setUpdatingStatus(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/applications/${appId}/status?status=${status}`, {}, { 
        headers, 
        withCredentials: true 
      });
      setApplication(prev => ({ ...prev, status }));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    
    setSubmittingNote(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/applications/${appId}/notes?note=${encodeURIComponent(noteText)}`, 
        {}, 
        { headers, withCredentials: true }
      );
      setApplication(response.data);
      setNoteText('');
      toast.success('Note added');
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmittingNote(false);
    }
  };

  const getStatusBadge = (status) => {
    const cfg = getStageConfig(status);
    const Icon = cfg.icon;
    return (
      <Badge className={`border ${cfg.badge} px-3 py-1`}>
        <Icon className="w-4 h-4 mr-1" /> {cfg.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6 animate-fadeIn" data-testid="application-detail-page">
        {/* Back Button */}
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Applications
        </Button>

        {/* Application Header */}
        <Card>
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
              <div className="flex gap-4">
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  {application.candidate?.picture ? (
                    <img src={application.candidate.picture} alt="" className="w-16 h-16 rounded-xl object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-slate-400" />
                  )}
                </div>
                <div>
                  <div className="mb-2">
                    {getStatusBadge(normalizeStatus(application.status))}
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                    {isRecruiter ? application.candidate?.name : application.job?.title}
                  </h1>
                  <p className="text-lg text-slate-600">
                    {isRecruiter ? `Applied for ${application.job?.title}` : application.job?.location}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    Applied on {new Date(application.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', month: 'long', day: 'numeric'
                    })}
                  </p>
                </div>
              </div>

              {isRecruiter && (
                <div className="flex flex-col gap-2">
                  <Select 
                    value={normalizeStatus(application.status)} 
                    onValueChange={handleStatusChange}
                    disabled={updatingStatus}
                  >
                    <SelectTrigger className="w-48" data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ALL_STAGES.map(s => (
                        <SelectItem key={s.key} value={s.key} data-testid={`status-option-${s.key}`}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-6">
            {/* Job Details — always visible so candidates can see what they applied for */}
            {application.job && (
              <Card data-testid="applied-job-details">
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <CardTitle style={{ fontFamily: 'Outfit' }}>Job Details</CardTitle>
                    <Link to={`/jobs/${application.job.job_id}`}>
                      <Button variant="outline" size="sm" data-testid="view-full-job-btn">View Full Job</Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="font-semibold text-slate-900 text-lg">{application.job.title}</p>
                    <p className="text-sm text-slate-500">
                      {application.job.location}
                      {application.job.job_type && <> · {String(application.job.job_type).replace('_', ' ')}</>}
                      {application.job.experience_years > 0 && <> · {application.job.experience_years}+ yrs</>}
                    </p>
                    {(application.job.salary_min || application.job.salary_max) && (
                      <p className="text-sm text-slate-600 mt-1">
                        Salary: {application.job.salary_min ? `₹${application.job.salary_min.toLocaleString('en-IN')}` : ''}
                        {application.job.salary_max ? ` - ₹${application.job.salary_max.toLocaleString('en-IN')}` : ''}
                      </p>
                    )}
                  </div>
                  {application.job.description && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1 text-sm">Description</h4>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-6">{application.job.description}</p>
                    </div>
                  )}
                  {application.job.requirements && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1 text-sm">Requirements</h4>
                      <p className="text-sm text-slate-700 whitespace-pre-wrap line-clamp-4">{application.job.requirements}</p>
                    </div>
                  )}
                  {application.job.skills_required?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1 text-sm">Skills</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {application.job.skills_required.map((s, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Cover Letter */}
            {application.cover_letter && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Cover Letter</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 whitespace-pre-wrap">{application.cover_letter}</p>
                </CardContent>
              </Card>
            )}

            {/* Candidate Profile (for recruiters) */}
            {isRecruiter && application.candidate_profile && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Candidate Profile</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {application.candidate_profile.skills?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Skills</h4>
                      <div className="flex flex-wrap gap-2">
                        {application.candidate_profile.skills.map((skill, idx) => (
                          <Badge key={idx} variant="outline">{skill}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {application.candidate_profile.experience_years && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1">Experience</h4>
                      <p className="text-slate-600">{application.candidate_profile.experience_years} years</p>
                    </div>
                  )}

                  {application.candidate_profile.work_experience?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Work Experience</h4>
                      <div className="space-y-3">
                        {application.candidate_profile.work_experience.map((exp, idx) => (
                          <div key={idx} className="border-l-2 border-indigo-200 pl-4">
                            <p className="font-medium text-slate-900">{exp.title}</p>
                            <p className="text-sm text-slate-600">{exp.company}</p>
                            {exp.duration && <p className="text-sm text-slate-500">{exp.duration}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {application.candidate_profile.education?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Education</h4>
                      <div className="space-y-2">
                        {application.candidate_profile.education.map((edu, idx) => (
                          <div key={idx}>
                            <p className="font-medium text-slate-900">{edu.degree}</p>
                            <p className="text-sm text-slate-600">{edu.institution} {edu.year && `(${edu.year})`}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Notes (for recruiters) */}
            {isRecruiter && (
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Outfit' }}>Notes</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Textarea
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note about this candidate..."
                      className="flex-1"
                      data-testid="note-input"
                    />
                    <Button onClick={handleAddNote} disabled={submittingNote || !noteText.trim()} data-testid="add-note-btn">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {application.notes?.length > 0 ? (
                    <div className="space-y-3 pt-4 border-t">
                      {application.notes.map((note, idx) => (
                        <div key={note.note_id || idx} className="bg-slate-50 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <MessageSquare className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-sm text-slate-900">{note.author_name}</span>
                            <span className="text-xs text-slate-500">
                              {new Date(note.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-slate-700">{note.text}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-4">No notes yet</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Info */}
            {isRecruiter && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Contact Info</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-slate-400" />
                    <a href={`mailto:${application.candidate?.email}`} className="text-indigo-600 hover:underline">
                      {application.candidate?.email}
                    </a>
                  </div>
                  {application.candidate_profile?.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-slate-400" />
                      <span>{application.candidate_profile.phone}</span>
                    </div>
                  )}
                  {application.candidate_profile?.linkedin && (
                    <div className="flex items-center gap-3 text-sm">
                      <Linkedin className="w-4 h-4 text-slate-400" />
                      <a href={application.candidate_profile.linkedin} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                        LinkedIn
                      </a>
                    </div>
                  )}
                  {application.candidate_profile?.portfolio && (
                    <div className="flex items-center gap-3 text-sm">
                      <Globe className="w-4 h-4 text-slate-400" />
                      <a href={application.candidate_profile.portfolio} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">
                        Portfolio
                      </a>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Job Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Job Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-slate-500">Position:</span>
                  <p className="font-medium text-slate-900">{application.job?.title}</p>
                </div>
                <div>
                  <span className="text-slate-500">Location:</span>
                  <p className="font-medium text-slate-900">{application.job?.location}</p>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base" style={{ fontFamily: 'Outfit' }}>Timeline</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full"></div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Application Submitted</p>
                      <p className="text-xs text-slate-500">
                        {new Date(application.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {application.updated_at !== application.created_at && (
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-600 rounded-full"></div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">Last Updated</p>
                        <p className="text-xs text-slate-500">
                          {new Date(application.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApplicationDetailPage;
